/**
 * Link Preview inspector — shows how a page on this site unfurls when
 * shared (iMessage, RCS, Slack), without pasting links into a real chat.
 *
 * Fetches the page the way a preview crawler does (raw HTML, no
 * JavaScript), reads its Open Graph tags, and renders message-app-style
 * mockups from them. Same-origin paths only: crawler-injected tags come
 * from this server, and cross-origin fetches are blocked by CORS anyway.
 */
import { Button } from "@bandwith/ui";
import { useQuery } from "@tanstack/react-query";
import { Link2, MessageCircle } from "lucide-react";
import { type FormEvent, useState } from "react";
import { linkPreviewRoute } from "../router";

interface PreviewTags {
  pageTitle: string | null;
  title: string | null;
  description: string | null;
  image: string | null;
  url: string | null;
  siteName: string | null;
}

function parsePreviewTags(html: string): PreviewTags {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const meta = (selector: string) =>
    doc.querySelector(`meta[${selector}]`)?.getAttribute("content") ?? null;
  return {
    pageTitle: doc.querySelector("title")?.textContent ?? null,
    title: meta('property="og:title"'),
    description: meta('property="og:description"'),
    image: meta('property="og:image"'),
    url: meta('property="og:url"'),
    siteName: meta('property="og:site_name"'),
  };
}

/**
 * Turn user input into a same-origin path, or explain why we can't.
 * Accepts "/setlists/abc", full URLs on this origin, and full URLs on
 * other Bandwith deploys (same path, fetched from this one). All input
 * is canonicalized through URL parsing and reduced to pathname + search,
 * so the fetch can never leave this origin — a protocol-relative
 * "//host/x" keeps only "/x".
 */
function normalizePath(input: string): { path?: string; error?: string } {
  const trimmed = input.trim();
  if (trimmed === "") return { error: "Enter a path to preview." };
  try {
    const url = new URL(trimmed, window.location.origin);
    return { path: url.pathname + url.search };
  } catch {
    return {
      error: "Enter a path like /setlists/abc123 or a full URL.",
    };
  }
}

function usePreviewTags(path: string | undefined) {
  return useQuery({
    queryKey: ["link-preview", path],
    enabled: path !== undefined,
    retry: false,
    queryFn: async () => {
      // Enforce same-origin on the parsed URL, not the string shape:
      // "//host/x" and backslash variants parse as other-origin URLs.
      const target = new URL(path as string, window.location.origin);
      if (target.origin !== window.location.origin) {
        throw new Error("Only paths on this site can be previewed.");
      }
      const response = await fetch(target, {
        headers: { accept: "text/html" },
      });
      if (!response.ok) {
        throw new Error(`Fetching ${path} failed (${response.status})`);
      }
      return parsePreviewTags(await response.text());
    },
  });
}

/** Host shown under the card title; falls back past a malformed og:url. */
function hostOf(url: string | null): string {
  if (url) {
    try {
      return new URL(url).host;
    } catch {
      // Fall through to the current host
    }
  }
  return window.location.host;
}

/** The compact card iMessage and RCS render under a shared link. */
function MessageCardMock({ tags }: { tags: PreviewTags }) {
  const title = tags.title ?? tags.pageTitle ?? "Untitled";
  const host = hostOf(tags.url);
  return (
    <div className="max-w-sm overflow-hidden rounded-2xl border border-border-subtle bg-bg-tertiary">
      {tags.image && (
        // biome-ignore lint/performance/noImgElement: Shows the raw og:image exactly as a chat app fetches it
        <img
          src={tags.image}
          alt={`Preview card for ${title}`}
          className="aspect-[1200/630] w-full object-cover"
        />
      )}
      <div className="px-3 py-2">
        <div className="truncate text-sm font-semibold text-text-primary">
          {title}
        </div>
        <div className="truncate text-xs text-text-muted">{host}</div>
      </div>
    </div>
  );
}

/** The expanded unfurl style Slack and Discord use. */
function UnfurlMock({ tags }: { tags: PreviewTags }) {
  const title = tags.title ?? tags.pageTitle ?? "Untitled";
  return (
    <div className="max-w-md border-l-4 border-border-accent pl-3">
      {tags.siteName && (
        <div className="text-xs font-medium text-text-muted">
          {tags.siteName}
        </div>
      )}
      <div className="text-sm font-semibold text-amber">{title}</div>
      {tags.description && (
        <div className="mt-0.5 text-sm text-text-secondary">
          {tags.description}
        </div>
      )}
      {tags.image && (
        // biome-ignore lint/performance/noImgElement: Shows the raw og:image exactly as a chat app fetches it
        <img
          src={tags.image}
          alt={`Preview card for ${title}`}
          className="mt-2 aspect-[1200/630] w-full max-w-sm rounded-md"
        />
      )}
    </div>
  );
}

function TagTable({ tags }: { tags: PreviewTags }) {
  const rows: [string, string | null][] = [
    ["<title>", tags.pageTitle],
    ["og:title", tags.title],
    ["og:description", tags.description],
    ["og:image", tags.image],
    ["og:url", tags.url],
    ["og:site_name", tags.siteName],
  ];
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([name, value]) => (
          <tr key={name} className="border-t border-border-subtle">
            <td className="whitespace-nowrap py-1.5 pr-4 align-top font-mono text-text-muted">
              {name}
            </td>
            <td className="break-all py-1.5 font-mono text-text-secondary">
              {value ?? <span className="text-text-disabled">missing</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function LinkPreview() {
  const { path } = linkPreviewRoute.useSearch();
  const navigate = linkPreviewRoute.useNavigate();
  const [input, setInput] = useState(path ?? "");
  const [inputError, setInputError] = useState<string | null>(null);
  const query = usePreviewTags(path);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const { path: normalized, error } = normalizePath(input);
    setInputError(error ?? null);
    if (normalized) {
      void navigate({ search: { path: normalized } });
    }
  };

  return (
    <div className="space-y-8 pb-24">
      <section>
        <h1 className="mb-2 flex items-center gap-2 text-2xl font-bold text-text-primary">
          <MessageCircle className="h-6 w-6 text-amber" aria-hidden="true" />
          Link Preview
        </h1>
        <p className="text-sm text-text-muted">
          See how a song or setlist link unfurls in iMessage, RCS, or Slack.
          Fetches the page like a preview crawler (no JavaScript) and renders
          its Open Graph tags.
        </p>
      </section>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="/setlists/abc123 or a full URL"
          className="min-w-0 flex-1 rounded-md border border-border-subtle bg-bg-secondary px-3 py-2 font-mono text-sm text-text-primary placeholder:text-text-disabled focus:border-amber focus:outline-none"
          aria-label="Path or URL to preview"
        />
        <Button type="submit">
          <Link2 className="h-4 w-4" />
          Preview
        </Button>
      </form>
      {inputError && <p className="text-sm text-red-muted">{inputError}</p>}

      {query.isLoading && (
        <p className="text-sm text-text-muted">Fetching preview…</p>
      )}
      {query.isError && (
        <p className="text-sm text-red-muted">{query.error.message}</p>
      )}

      {query.data && (
        <div className="space-y-8" data-testid="link-preview-results">
          <section className="space-y-3">
            <h2 className="text-xs font-medium uppercase tracking-wide text-text-muted">
              iMessage / RCS
            </h2>
            <MessageCardMock tags={query.data} />
          </section>

          <section className="space-y-3">
            <h2 className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Slack / Discord
            </h2>
            <UnfurlMock tags={query.data} />
          </section>

          <section className="space-y-3">
            <h2 className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Tags
            </h2>
            <TagTable tags={query.data} />
          </section>
        </div>
      )}
    </div>
  );
}
